import React, { useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import { getActivityAnchorId } from '@aline/common';
import { link } from '@aline/routes';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import {
  ExternalLink,
  Link as LinkIcon,
  Mail,
  MessageCircleMore,
  MoreHorizontal,
  Pencil,
  Sparkle,
  Trash2,
} from 'lucide-react';
import type { OnSubmitCommentReturn } from 'src/components';
import { SmallAlineButton } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import { SkillElementPlugin } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/plugins/skill-entity-kit';
import type { AlineMarkdownEditorRef } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { normalizeBlankMarkdown } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/empty-paragraph';
import { ChatEditorMarkdownPlugin } from 'src/components/ai/chat/ChatPanel/ChatEditor';
import { AlineAiAvatar } from 'src/components/AlineAvatar';
import { SlackIcon } from 'src/components/icons/SlackIcon';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  domElIdCreators,
  formatRelativeTime,
  getAssigneeDisplayDetails,
  openLink,
  useDispatchNotification,
} from 'src/helpers';
import { useDeleteComment, useDeleteCommentReply } from 'src/hooks';
import { displayOnHover } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import type { ActivityComment } from './activity-types';
import { ActivityReplyComposer } from './ActivityReplyComposer';

interface ChannelEmailDetails {
  attachments: { documentId: UUID; name: string }[];
  fromAddress: string;
  fromName: string | null;
  subject: string;
}

// Card border per the activity-feed design; no divider token exists in appColors yet.
const DIVIDER_COLOR = '#E2E8F0';
/**
 * A comment that asked Aline AI can carry skills as `skill:` links. These show them as chips,
 * and keep them as links when the comment is edited and saved again. They add no `/` picker.
 */
const COMMENT_SKILL_PLUGINS = [ChatEditorMarkdownPlugin, SkillElementPlugin];

const useStyles = makeStyles()(theme => ({
  actionIcon: {
    color: theme.appColors.iconsType,
    opacity: 0.5,
  },
  actions: {
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.1s ease',
  },
  actor: {
    fontWeight: 600,
  },
  attachment: {
    background: 'none',
    border: 'none',
    color: theme.appColors.primary,
    cursor: 'pointer',
    fontSize: 14,
    padding: 0,
    textAlign: 'left',
  },
  attachments: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  body: {
    color: theme.appColors.iconsType,
  },
  card: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${DIVIDER_COLOR}`,
    borderRadius: 8,
    boxShadow: '0px 0px 3.4px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.5),
    width: '100%',
  },
  divider: {
    borderTop: `1px solid ${DIVIDER_COLOR}`,
    width: '100%',
  },
  emailFrom: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  emailSubject: {
    fontSize: 14,
    fontWeight: 600,
  },
  entry: {
    [`&:hover .${displayOnHover}, &:focus-within .${displayOnHover}`]: {
      opacity: 1,
      pointerEvents: 'auto',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    minHeight: 22,
  },
  menuIcon: {
    color: theme.appColors.iconsType,
    marginRight: theme.spacing(1),
    opacity: 0.75,
  },
  meta: {
    alignItems: 'baseline',
    columnGap: theme.spacing(1),
    display: 'flex',
    flexGrow: 1,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  saveRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  sentence: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    lineHeight: 1.4,
  },
  time: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    opacity: 0.5,
    whiteSpace: 'nowrap',
  },
}));

interface ActivityCommentCardProps {
  /** Feed row this card renders — the anchor a copied link points at. */
  activityItemId: UUID;
  comment: ActivityComment;
  workflowId: UUID;
  /** Inbound email shown on an email-sourced anchor. */
  email?: ChannelEmailDetails | null;
  /** AI-sourced comment: show the top-level author as "Aline AI" rather than the acting user. */
  isAi?: boolean;
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  /** When the anchor is a Slack thread, its header shows a Slack mark + an "open in Slack" action. */
  slackThreadUrl?: string | null;
  /** Origin of the anchor entry. Slack and email change the header glyph and verb. */
  sourceType?: Apollo.ActivitySourceType;
}

export function ActivityCommentCard({
  activityItemId,
  comment,
  email,
  isAi = false,
  onSubmitReply,
  slackThreadUrl,
  sourceType,
  workflowId,
}: ActivityCommentCardProps): JSX.Element | null {
  const { classes } = useStyles();
  const { deleteComment } = useDeleteComment();
  const { deleteCommentReply } = useDeleteCommentReply();
  const [deleted, setDeleted] = useState(false);
  const [deletedReplyIds, setDeletedReplyIds] = useState<UUID[]>([]);

  async function handleDeleteComment() {
    const { data } = await deleteComment(
      comment.id,
      comment.commentable.id,
      comment.commentable.__typename,
      { variables: { input: { id: comment.id } } }
    );
    if (data) setDeleted(true);
  }

  async function handleDeleteReply(replyId: UUID) {
    const { data } = await deleteCommentReply(comment.id, replyId, {
      variables: { input: { id: replyId } },
    });
    if (data) setDeletedReplyIds(prev => [...prev, replyId]);
  }

  if (deleted) return null;

  return (
    <div className={classes.card}>
      <CommentEntry
        activityItemId={activityItemId}
        comment={comment}
        domId={domElIdCreators.commentEl(comment.id)}
        email={email}
        isAi={isAi}
        slackThreadUrl={slackThreadUrl}
        sourceType={sourceType}
        verb={getCommentVerb(sourceType)}
        workflowId={workflowId}
        onDelete={handleDeleteComment}
      />
      {comment.replies
        .filter(reply => !deletedReplyIds.includes(reply.id))
        .map(reply => (
          <React.Fragment key={reply.id}>
            <div className={classes.divider} />
            <CommentEntry
              activityItemId={activityItemId}
              comment={reply}
              domId={domElIdCreators.commentEl(reply.id)}
              email={reply.email}
              isAi={reply.isAiAuthored}
              parentComment={comment}
              verb="responded"
              workflowId={workflowId}
              onDelete={() => handleDeleteReply(reply.id)}
            />
          </React.Fragment>
        ))}
      {onSubmitReply && (
        <>
          <div className={classes.divider} />
          <ActivityReplyComposer commentId={comment.id} onSubmitReply={onSubmitReply} />
        </>
      )}
    </div>
  );
}

type ReplyComment = ActivityComment['replies'][number];

interface OptimisticCommentUpdateArgs {
  comment: ActivityComment | ReplyComment;
  text: string;
  parentComment?: ActivityComment;
}

interface CommentEntryProps {
  /**
   * Row this entry belongs to. Copied links anchor on it rather than on the comment, because the
   * feed can seek a window around an activity item — a comment older than the loaded page has no
   * row to scroll to. A reply anchors on its parent's row, which is the card it renders inside.
   */
  activityItemId: UUID;
  comment: ActivityComment | ReplyComment;
  /** DOM id so a freshly-posted reply can be scrolled into view. */
  domId: string;
  onDelete: () => Promise<void>;
  verb: string;
  workflowId: UUID;
  /** Inbound email rendered above the anchor body. */
  email?: ChannelEmailDetails | null;
  /** Written by Aline AI: display the author as "Aline AI" with the AI avatar. */
  isAi?: boolean;
  /** Root comment used to synthesize a CommentSimple-shaped optimistic reply update. */
  parentComment?: ActivityComment;
  /** Slack deep link — when set, the header shows a Slack mark and an "open in Slack" menu item. */
  slackThreadUrl?: string | null;
  /** Origin of the entry. Slack and email change the header glyph. */
  sourceType?: Apollo.ActivitySourceType;
}

function CommentEntry({
  activityItemId,
  comment,
  domId,
  email,
  isAi = false,
  onDelete,
  parentComment,
  slackThreadUrl,
  sourceType,
  verb,
  workflowId,
}: CommentEntryProps): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [editing, setEditing] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const dispatchLinkCopied = useDispatchNotification({
    message: 'Comment link copied',
    variant: 'success',
  });
  const dispatchCopyFailed = useDispatchNotification({
    message: "Couldn't copy comment link",
    variant: 'error',
  });
  const menuOpen = Boolean(menuAnchorEl);
  const actorName = isAi
    ? 'Aline AI'
    : comment.author
      ? getAssigneeDisplayDetails(comment.author).primary
      : (comment.importedAuthorName ?? 'Someone');
  const text = comment.text.trim();
  const canEdit = Boolean(currentUser && !isAi && comment.author?.id === currentUser.id);

  function handleCopyLink(): void {
    const url = new URL(window.location.href);
    url.hash = getActivityAnchorId(activityItemId);
    navigator.clipboard
      .writeText(url.toString())
      .then(dispatchLinkCopied)
      .catch(dispatchCopyFailed);
  }

  return (
    <div className={classes.entry} id={domId}>
      <div className={classes.header}>
        <CommentEntryGlyph isAi={isAi} sourceType={sourceType} />
        <div className={classes.meta}>
          <Typography className={classes.sentence} component="span" variant="body2">
            <span className={classes.actor}>{actorName}</span> {verb}
          </Typography>
          <Typography className={classes.time} component="span" variant="caption">
            {formatRelativeTime(comment.createdAt)}
          </Typography>
        </div>
        <IconButton
          aria-label="Comment options"
          className={`${classes.actions} ${displayOnHover}`}
          size="small"
          onClick={event => setMenuAnchorEl(event.currentTarget)}
        >
          <MoreHorizontal className={classes.actionIcon} size={16} />
        </IconButton>
        <Menu
          anchorEl={menuAnchorEl}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          open={menuOpen}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          onClose={() => setMenuAnchorEl(null)}
        >
          {canEdit && (
            <MenuItem
              dense
              onClick={() => {
                setMenuAnchorEl(null);
                setEditing(true);
              }}
            >
              <Pencil className={classes.menuIcon} size={16} />
              Edit
            </MenuItem>
          )}
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              void onDelete();
            }}
          >
            <Trash2 className={classes.menuIcon} size={16} />
            Delete
          </MenuItem>
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              handleCopyLink();
            }}
          >
            <LinkIcon className={classes.menuIcon} size={16} />
            Copy link to comment
          </MenuItem>
          {slackThreadUrl && (
            <MenuItem
              dense
              onClick={() => {
                setMenuAnchorEl(null);
                openLink(slackThreadUrl);
              }}
            >
              <ExternalLink className={classes.menuIcon} size={16} />
              View in Slack
            </MenuItem>
          )}
        </Menu>
      </div>
      {email && <EmailMessageDetails email={email} />}
      {editing ? (
        <CommentMarkdownEditor
          comment={comment}
          initialMarkdown={text}
          parentComment={parentComment}
          workflowId={workflowId}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <div className={classes.body}>
          <AlineMarkdownEditor
            noBorder
            readOnly
            extraPlugins={COMMENT_SKILL_PLUGINS}
            maxHeight="none"
            minHeight={0}
            value={text}
          />
        </div>
      )}
    </div>
  );
}

const COMMENT_VERBS = {
  [Apollo.ActivitySourceType.Ai]: 'added a comment',
  [Apollo.ActivitySourceType.Email]: 'sent an email',
  [Apollo.ActivitySourceType.Slack]: 'started a Slack thread',
  [Apollo.ActivitySourceType.System]: 'added a comment',
  [Apollo.ActivitySourceType.Teams]: 'added a comment',
  [Apollo.ActivitySourceType.Web]: 'added a comment',
} as const satisfies Record<Apollo.ActivitySourceType, string>;

function getCommentVerb(sourceType: Apollo.ActivitySourceType | undefined) {
  if (!sourceType) return 'added a comment';
  return COMMENT_VERBS[sourceType];
}

function CommentEntryGlyph({
  isAi,
  sourceType,
}: {
  isAi: boolean;
  sourceType?: Apollo.ActivitySourceType;
}) {
  if (isAi)
    return (
      <AlineAiAvatar aria-hidden styles={{ size: 'xxs' }}>
        <Sparkle size={10} strokeWidth={2} />
      </AlineAiAvatar>
    );
  return <CommentSourceIcon sourceType={sourceType} />;
}

function CommentSourceIcon({ sourceType }: { sourceType?: Apollo.ActivitySourceType }) {
  const { theme } = useStyles();
  if (!sourceType)
    return (
      <MessageCircleMore color={theme.appColors.primary} size={16} style={{ flexShrink: 0 }} />
    );

  switch (sourceType) {
    case Apollo.ActivitySourceType.Email:
      return <Mail color={theme.appColors.primary} size={16} style={{ flexShrink: 0 }} />;
    case Apollo.ActivitySourceType.Slack:
      return <SlackIcon height={16} style={{ flexShrink: 0 }} width={16} />;
    case Apollo.ActivitySourceType.Ai:
    case Apollo.ActivitySourceType.System:
    case Apollo.ActivitySourceType.Teams:
    case Apollo.ActivitySourceType.Web:
      return (
        <MessageCircleMore color={theme.appColors.primary} size={16} style={{ flexShrink: 0 }} />
      );
  }
}

function EmailMessageDetails({ email }: { email: ChannelEmailDetails }) {
  const { classes } = useStyles();
  const from = email.fromName ? `${email.fromName} <${email.fromAddress}>` : email.fromAddress;

  return (
    <div>
      <Typography className={classes.emailSubject}>{email.subject}</Typography>
      <Typography className={classes.emailFrom}>From {from}</Typography>
      {email.attachments.length > 0 && (
        <div className={classes.attachments}>
          {email.attachments.map(attachment => (
            <button
              key={attachment.documentId}
              className={classes.attachment}
              type="button"
              onClick={() =>
                openLink(link.DocumentDetail({ documentId: attachment.documentId }), {
                  title: attachment.name,
                })
              }
            >
              {attachment.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentMarkdownEditor({
  comment,
  initialMarkdown,
  onCancel,
  onSaved,
  parentComment,
  workflowId,
}: {
  comment: ActivityComment | ReplyComment;
  initialMarkdown: string;
  onCancel: () => void;
  onSaved: () => void;
  workflowId: UUID;
  parentComment?: ActivityComment;
}) {
  const { classes } = useStyles();
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const [updateComment, { loading }] = Apollo.useUpdateCommentMutation();
  const pristine = markdown === initialMarkdown;

  async function handleSave() {
    const text = normalizeBlankMarkdown((editorRef.current?.getMarkdown() ?? markdown).trim());
    if (!text) return;

    await updateComment({
      optimisticResponse: getOptimisticUpdateCommentResponse({
        comment,
        parentComment,
        text,
      }),
      variables: {
        input: {
          id: comment.id,
          props: { text },
        },
      },
    });
    onSaved();
  }

  return (
    <div>
      <AlineMarkdownEditor
        ref={editorRef}
        autoFocus
        extraPlugins={COMMENT_SKILL_PLUGINS}
        maxHeight={180}
        mentions={{ workflowId }}
        minHeight={72}
        placeholder="Edit comment..."
        style={{ borderRadius: 8 }}
        value={initialMarkdown}
        onChange={setMarkdown}
        onSubmit={handleSave}
      />
      <div className={classes.saveRow}>
        <SmallAlineButton
          color="primary"
          disabled={pristine || loading || !normalizeBlankMarkdown(markdown.trim())}
          loading={loading}
          variant="contained"
          onClick={handleSave}
        >
          Save
        </SmallAlineButton>
        <SmallAlineButton color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </SmallAlineButton>
      </div>
    </div>
  );
}

function getOptimisticUpdateCommentResponse({
  comment,
  parentComment,
  text,
}: OptimisticCommentUpdateArgs): Apollo.UpdateCommentMutation {
  return {
    __typename: 'Mutation',
    updateComment: {
      __typename: 'CommentResponse',
      comment: getOptimisticUpdatedComment({ comment, parentComment, text }),
    },
  };
}

function getOptimisticUpdatedComment({
  comment,
  parentComment,
  text,
}: OptimisticCommentUpdateArgs): NonNullable<
  Apollo.UpdateCommentMutation['updateComment']['comment']
> {
  if (getIsActivityComment(comment)) {
    return {
      ...comment,
      text,
    };
  }
  if (!parentComment)
    throw new Error('Reply comments require a parent comment for optimistic updates.');

  return {
    ...comment,
    commentable: parentComment.commentable,
    deletedAt: null,
    parent: {
      __typename: 'Comment',
      id: parentComment.id,
    },
    replies: [],
    text,
  };
}

function getIsActivityComment(comment: ActivityComment | ReplyComment): comment is ActivityComment {
  return 'commentable' in comment;
}
